import { MEGA_ADMIN_ROLE_NAME, ROLES } from '@/core/security/roles';
import type { EntityDetail, FeaturePermissions, User, UserProfile, UserRole } from '../models';

/**
 * The API reports a display role ("Mega Admin"); `core/security` and the
 * sidebar's role filter work in slugs ('admin'). Anything unrecognised maps to
 * no role, which means guarded routes stay closed rather than open.
 *
 * Mega Admin collapses into 'admin' here on purpose -- the shell tells the two
 * apart through `User.roleName` (see `isMegaAdminRoleName`), not through a
 * separate permission tier.
 */
const ROLE_NAME_TO_APP_ROLE: Record<string, string> = {
  [MEGA_ADMIN_ROLE_NAME]: ROLES.ADMIN,
  'super admin': ROLES.ADMIN,
  admin: ROLES.ADMIN,
  initiator: ROLES.INITIATOR,
  requester: ROLES.INITIATOR,
  supplier: ROLES.SUPPLIER,
  vendor: ROLES.SUPPLIER,
};

/**
 * The one place that knows GET /users/me sends `{ id, name }` while
 * loginapi.md documents `{ roleId, roleName }`. `name` wins because that is
 * what the deployed API actually returns.
 */
export function roleNameOf(role: UserRole | null | undefined): string | undefined {
  const raw = role?.name ?? role?.roleName;
  const trimmed = raw?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * The role's granted features, defended against a payload that does not carry them.
 *
 * `/users/me` is the only response that fills this in, and an older platform does not fill it at all —
 * so anything but a well-formed array reads as "nothing known", never as "nothing granted". The two are
 * different, and only the platform decides the second.
 */
export function featuresAndPermissionsOf(
  role: UserRole | null | undefined,
): FeaturePermissions[] | undefined {
  /*
    `undefined` and `[]` mean different things and the difference decides what the sidebar does.

    Not an array at all is "the platform did not tell us" — an older platform, a payload that changed
    shape — and nothing may be hidden on the strength of it. An EMPTY array is the platform answering
    "this role holds nothing", which is a real answer: a role an administrator has created but not yet
    ticked anything for must see no feature menus, not all of them. Collapsing the two, as this used to,
    made an unconfigured role look like an unanswered one and showed it everything.
  */
  if (!Array.isArray(role?.featuresAndPermissions)) return undefined;

  return role.featuresAndPermissions.filter(
    (entry): entry is FeaturePermissions =>
      typeof entry?.feature === 'string' && Array.isArray(entry.permissions),
  );
}

/**
 * The legal entities `GET /users/me` reports the user is assigned to.
 *
 * Same unknown-versus-empty rule as the grants above, and for the same reason: `undefined` is "the
 * platform did not tell us" — an older platform, or the ordinary user read, which reports null because it
 * does not resolve assignments — while an EMPTY array is the platform answering "assigned to nothing",
 * which is the ordinary state of a Mega Admin. A sidebar that collapsed the two could not tell a user
 * whose entities failed to load from one who genuinely has none.
 *
 * Entries are filtered rather than trusted: an id that is not a number, or a blank name, is not something
 * a list can show.
 */
export function entityDetailOf(
  profile: Pick<UserProfile, 'entityDetail'> | null | undefined,
): EntityDetail[] | undefined {
  if (!Array.isArray(profile?.entityDetail)) return undefined;

  return profile.entityDetail.filter(
    (entry): entry is EntityDetail =>
      typeof entry?.id === 'number' && typeof entry.name === 'string' && entry.name.trim() !== '',
  );
}

export function mapRoleName(roleName: string | null | undefined): string[] {
  if (!roleName) return [];
  const slug = ROLE_NAME_TO_APP_ROLE[roleName.trim().toLowerCase()];
  return slug ? [slug] : [];
}

function joinName(profile: UserProfile): string {
  const parts = [profile.firstName, profile.lastName]
    .map((part) => part?.trim() ?? '')
    .filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : profile.userName;
}

/** GET /users/me payload -> the app's User. */
export function profileToUser(profile: UserProfile): User {
  const emailAddress = profile.emailAddress ?? '';
  const roleName = roleNameOf(profile.role);
  const granted = featuresAndPermissionsOf(profile.role);
  const entities = entityDetailOf(profile);

  return {
    id: String(profile.id),
    userName: profile.userName,
    firstName: profile.firstName ?? '',
    lastName: profile.lastName ?? '',
    emailAddress,
    name: joinName(profile),
    email: emailAddress,
    roles: mapRoleName(roleName),
    ...(roleName ? { roleName } : {}),
    /* Carried through whenever the platform reported it, empty included -- see featuresAndPermissionsOf. */
    ...(granted !== undefined ? { featuresAndPermissions: granted } : {}),
    /* Same rule, same reason -- see entityDetailOf. */
    ...(entities !== undefined ? { entityDetail: entities } : {}),
    ...(profile.jobTitle ? { jobTitle: profile.jobTitle } : {}),
  };
}

/**
 * Stand-in used when the profile call fails.
 *
 * loginapi.md section 8 says a failed profile fetch should still "proceed to
 * dashboard with limited access", so the session stays valid and we show what
 * little we know -- the identifier the user typed.
 */
export function fallbackUser(identifier: string): User {
  return {
    id: '',
    userName: identifier,
    firstName: '',
    lastName: '',
    emailAddress: '',
    name: identifier,
    email: '',
    roles: [],
  };
}
